import AccessPassExclusiveHeading from '@/components/AccessPassExclusiveHeading';
import APEmptySection from '@/components/APEmptySection';
import AccessPassIntro from '@/components/AccessPassIntro';
import Carousel from '@/components/Carousel';
import EventCard from '@/components/EventCard';
import Footer from '@/components/Footer';
import { HomeHeaderLeft, HomeHeaderRight } from '@/components/HomePageHeader';
import { EventCardsSkeleton } from '@/components/HomeSkeleton';
import MembershipUpsellSection from '@/components/MembershipUpsellSection';
import PendingPaymentBanner from '@/components/PendingPaymentBanner';
import PSHomeSectionEmpty from '@/components/PSHomeSectionEmpty';
import PSHomeSectionError from '@/components/PSHomeSectionError';
import PSUpsellBanner from '@/components/PSUpsellBanner';
import Tabs from '@/components/Tabs';
import TrialPassSection from '@/components/TrialPassSection';
import UpcomingBookingsSection from '@/components/UpcomingBookingsSection';
import PageFlatList from '@/components/ui/PageFlatList';
import { PAGE_GUTTER, useFrameWidth } from '@/components/ui/pageFrame';
import PSLinkButton from '@/components/ui/PSLinkButton';
import Divider from '@/components/ui/Divider';
import PSText from '@/components/ui/PSText';
import { space } from '@/constants/tokens';
import type { ShortEvent } from '@/src/api/eventsApi';
import {
  EXPERIENCE_FILTERS,
  useAllExperiences,
  type ExperienceFilter,
} from '@/src/hooks/useAllExperiences';
import { useHomePageEvents } from '@/src/hooks/useHomePageEvents';
import { useMembershipPlans } from '@/src/hooks/useMembershipPlans';
import { usePastMemberships } from '@/src/hooks/usePastMemberships';
import { restoreSession } from '@/src/hooks/useRestoredSession';
import {
  deriveMembershipUpsell,
  hasPlansOnOffer,
} from '@/src/lib/membershipUpsell';
import { useSessionUser } from '@/src/lib/sessionStore';
import { webUrl } from '@/src/utils/linkingUtils';
import { toast } from '@tamagui/toast/v2';
import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import type { Metadata } from 'expo-server';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type ListRenderItemInfo } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { getTokenValue, Text, View, XStack, YStack } from 'tamagui';
import { usePendingMembership } from '@/src/hooks/usePendingMembership';
import { useFetchReservations } from '@/src/hooks/useFetchReservations';
import type { MinimalBooking } from '@/src/api/bookingsApi';
import { differenceInHours, isFuture, parseISO } from 'date-fns';

const GRID_SKELETON_COUNT = 4;
// One row of bones under the rows already drawn, so a page in flight reads as
// "more coming" without dropping a screenful of empty slabs on the reader.
const GRID_LOAD_MORE_SKELETON_COUNT = 2;
const GRID_COLUMNS = 2;
/**
 * How early `onEndReached` fires, in screenfuls of scrolling left. Well above the
 * usual 0.5 because the distance FlatList measures runs to the end of the whole
 * page — and this page ends with a ~500px footer below the grid, which would eat
 * a smaller threshold whole and only page once the reader had passed the last row.
 */
const END_REACHED_THRESHOLD = 1.5;

// A stable identity for "no rows yet", so a fetch in flight doesn't hand the list
// a brand-new array every render.
const NO_EVENTS: ShortEvent[] = [];

const ALL_EXPERIENCES_HEADING: Record<ExperienceFilter, string> = {
  today: 'All experiences today',
  tomorrow: 'All experiences tomorrow',
  weekend: 'All experiences this weekend',
};

// The gap BETWEEN grid rows. `columnWrapperStyle` covers the gap inside a row;
// this is the other axis, and a separator is how a FlatList spaces rows without
// putting a gap above the header or below the footer too.
const GridRowGap = () => <View height={space.md} />;

const UPCOMING_WITHIN_HOURS = 48;

// Confirmed only: a pending/cancelled/refunded booking isn't one they're going to.
function isUpcoming(booking: MinimalBooking) {
  const start = parseISO(booking.slot.startDatetime);
  return (
    booking.status === 'confirmed' &&
    isFuture(start) &&
    differenceInHours(start, new Date()) < UPCOMING_WITHIN_HOURS
  );
}

const HOME_TITLE = 'Putting Scene';
const HOME_DESCRIPTION =
  "Discover Bangalore's best cultural, food, fitness, and creative events.";
const HOME_IMAGE_URL =
  'https://cdn.puttingscene.com/base/puttingscene-previewcard.png';

/**
 * SSR-only hook the server calls BEFORE rendering, injecting the result
 * straight into the response's `<head>` — see the fuller explanation on
 * `generateMetadata` in `app/events/[id]/index.tsx`.
 */
export async function generateMetadata(): Promise<Metadata> {
  const pageUrl = webUrl('/');

  return {
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    openGraph: {
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      url: pageUrl,
      type: 'website',
      images: [HOME_IMAGE_URL],
    },
    twitter: {
      card: 'summary_large_image',
      title: HOME_TITLE,
      description: HOME_DESCRIPTION,
      images: [HOME_IMAGE_URL],
    },
  };
}

export default function Home() {
  const router = useRouter();
  const user = useSessionUser();

  const { data, isLoading, error, retry, refresh } = useHomePageEvents();
  // The grid below the carousels is its own paged, filtered fetch (`GET /events`)
  // — a tab change or another page must not redraw the sections above it.
  const pendingMembership = usePendingMembership();
  const reservations = useFetchReservations();
  const refreshReservations = reservations.refresh;
  const retryReservations = reservations.retry;
  // The hook fetches once on mount, but home stays mounted through sign-in and
  // sign-out — refetch when the user changes so the list is theirs. The ref
  // skips mount, which the hook's own fetch already covers.
  const userId = user?.id ?? null;
  const lastUserId = useRef(userId);
  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    retryReservations();
  }, [userId, retryReservations]);
  const upcomingBookings = (reservations.reservations ?? [])
    .filter(isUpcoming)
    .sort(
      (a, b) =>
        parseISO(a.slot.startDatetime).getTime() -
        parseISO(b.slot.startDatetime).getTime()
    );
  const experiences = useAllExperiences();
  // Aliased so the callbacks below can depend on the callback itself (stable
  // across renders) rather than on the whole state object, which is rebuilt each
  // one.
  //
  //
  const refreshExperiences = experiences.refresh;
  const loadMoreExperiences = experiences.loadMore;
  const experiencesError = experiences.error;

  // Auto-select the first tab (in filter order) that actually has events, so a
  // reader lands on something worth looking at rather than an empty "today".
  // Turns itself off the moment a tab lands non-empty, a fetch errors, or the
  // reader taps a tab themselves — from then on tabs behave normally.
  const autoSelectingFilter = useRef(true);
  const autoSelectFilterIndex = useRef(0);

  useEffect(() => {
    if (!autoSelectingFilter.current || experiences.isLoading) return;
    if (experiences.error || experiences.events?.length) {
      autoSelectingFilter.current = false;
      return;
    }
    const nextIndex = autoSelectFilterIndex.current + 1;
    if (nextIndex < EXPERIENCE_FILTERS.length) {
      autoSelectFilterIndex.current = nextIndex;
      experiences.setFilter(EXPERIENCE_FILTERS[nextIndex].key);
    } else {
      // Every tab came back empty — settle back on today rather than leaving
      // the reader stranded on whichever tab happened to be checked last.
      autoSelectingFilter.current = false;
      if (experiences.filter !== 'today') experiences.setFilter('today');
    }
  }, [
    experiences.isLoading,
    experiences.events,
    experiences.error,
    experiences.filter,
    experiences.setFilter,
  ]);

  const onChangeExperienceFilter = useCallback(
    (next: ExperienceFilter) => {
      autoSelectingFilter.current = false;
      experiences.setFilter(next);
    },
    [experiences.setFilter]
  );

  // Failed or still in flight, the upsell simply doesn't draw — see the hook.
  const { plans } = useMembershipPlans();
  const pastMemberships = usePastMemberships();
  // Held back with the catalog until the history lands, or an ex-member sees the
  // trial offered and then pulled.
  const upsell = deriveMembershipUpsell({
    plans: (pastMemberships && plans) ?? [],
    user,
    pastMemberships: pastMemberships ?? [],
  });
  // `membership` is the entitled one only, so someone mid-checkout still reads
  // as a non-member here — which is what the upsell and the hero's tense want.
  const holdsAMembership = !!user?.membership;
  // Waits on the history: until it lands, an ex-member reads as `new`.
  const showsAccessPassIntro =
    pastMemberships !== null &&
    (upsell.state.kind === 'signed-out' || upsell.state.kind === 'new');
  const trialMembership =
    user?.membership?.plan.kind === 'trial' ? user.membership : null;
  // A zero-limit perk is one the trial doesn't include, not one it's used up.
  const trialPerks = Object.values(
    trialMembership?.perksUsage?.perks ?? {}
  ).filter((perk) => perk.isUnlimited || perk.limit > 0);

  // Derived, not measured: the two columns share the frame's content width minus
  // the single gap between them, and both numbers are known up front — so the
  // cards have their width on the first frame, with no layout pass to wait for.
  const frameWidth = useFrameWidth();
  const gridColW =
    (frameWidth - PAGE_GUTTER * 2 - space.md * (GRID_COLUMNS - 1)) /
    GRID_COLUMNS;

  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // The grid keeps whatever rows it has through a failure, so with rows on screen
  // the failure has nowhere to draw and toasts instead. With none, the grid draws
  // PSHomeSectionError itself and a toast would only say it twice.
  useEffect(() => {
    if (experiences.error && experiences.events?.length)
      toast.error(experiences.error.message);
  }, [experiences.error, experiences.events]);

  // Pull-to-refresh: the events and the user together, because half this page is
  // drawn off the user (the banner, the exclusive rail, the upsell), so a pass
  // bought elsewhere has to land in the same pull as the fresh events.
  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    // allSettled, not all: the spinner has to stop even when a leg fails, and
    // each already reports its own failure — events through `error` (toasted
    // above), a dead token by ending the session and flipping the header.
    // The grid is its own leg: it re-reads page 1 of the filter in hand.
    await Promise.allSettled([
      refresh(),
      refreshExperiences(),
      restoreSession(),
      refreshReservations(),
    ]);
    setIsRefreshing(false);
  }, [refresh, refreshExperiences, refreshReservations]);

  // A FAILED page is the one thing the list must not chase: `onEndReached` fires
  // again every time the reader hovers near the end, which would hammer a dead
  // endpoint. It waits for the retry link in the footer instead. (`loadMore`
  // itself no-ops with a page in flight or no cursor left, so the other repeat
  // calls FlatList makes cost nothing.)
  const onEndReached = useCallback(() => {
    if (!experiencesError) loadMoreExperiences();
  }, [experiencesError, loadMoreExperiences]);

  const renderExperience = useCallback(
    ({ item }: ListRenderItemInfo<ShortEvent>) => (
      <EventCard
        event={item}
        width={gridColW}
        onPress={() => router.push(`/events/${item.id}`)}
      />
    ),
    [gridColW, router]
  );

  // The component gallery is a dev-only surface. `__DEV__` is true only when
  // Metro serves the bundle from a local dev server, so in preview and
  // production builds the logo is inert.
  const logo = (
    <HomeHeaderLeft
      onPress={__DEV__ ? () => router.push('/components') : undefined}
    />
  );
  const authControl = (
    <HomeHeaderRight
      isMember={holdsAMembership}
      isLoggedIn={user !== null}
      onSignup={() => {
        router.push('/auth/mobile');
      }}
      onAccount={() => {
        // No account screen yet — reuse the auth entry as a stub for now.
        router.push('/profile');
      }}
    />
  );

  // Everything above the grid. It's the list's header rather than a sibling above
  // it because the grid can't be a list nested in a scroll view — see PageFlatList.
  const listHeader = (
    <>
      <View
        mx={-space.lg}
        // offset by the amount of space page flat list pushes content down
        top={-space.xl}
        bg={'$color.brandDark'}
      >
        {!holdsAMembership ? (
          <PSUpsellBanner
            ctaTitle="Check out Access Pass"
            // Lands on the landing page's All Plans block, not its hero — the
            // button names the plans, so that's where it goes.
            onPressCta={() =>
              router.push({
                pathname: '/memberships',
              })
            }
          />
        ) : (
          <View px={'$space.lg'} pb={'$space.lg'}>
            {/* gap, not a margin on the section: it renders null with nothing
                upcoming, and a gap collapses with it where a margin wouldn't. */}
            <YStack gap={'$space.xl'} mt={'$space.xl'}>
              {trialMembership && (
                <TrialPassSection
                  expiresAt={trialMembership.expiresAt}
                  perks={trialPerks}
                  onUpgrade={() =>
                    router.push({
                      pathname: '/memberships',
                      params: { section: 'plans' },
                    })
                  }
                />
              )}
              <UpcomingBookingsSection
                bookings={upcomingBookings}
                variant="dark"
              />
              <YStack gap={'$lg'}>
                <YStack gap={'$space.xs'}>
                  <PSText.Title color={'$color.textWhite'}>
                    Get first dibs and exclusive access
                  </PSText.Title>
                </YStack>
                {error && (
                  <PSHomeSectionError
                    kind={error.kind}
                    onRetry={retry}
                    isBackgroundDark
                  />
                )}
                {isLoading && (
                  <Carousel isBackgroundDark>
                    <EventCardsSkeleton />
                  </Carousel>
                )}
                {data &&
                  (data.accessPassExclusive.length === 0 ? (
                    <APEmptySection />
                  ) : (
                    <Carousel isBackgroundDark>
                      {data.accessPassExclusive.map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          panelBgColor="$panelTranslucent"
                          textColor="light"
                          onPress={() => router.push(`/events/${event.id}`)}
                        />
                      ))}
                    </Carousel>
                  ))}
              </YStack>
            </YStack>
            {hasPlansOnOffer(upsell) && (
              <View mt={'$space.xxl'}>
                <MembershipUpsellSection
                  upsell={upsell}
                  onSelectMembership={(plan) =>
                    router.push({
                      pathname: '/memberships',
                      params: { section: 'plans' },
                    })
                  }
                />
              </View>
            )}
          </View>
        )}
      </View>
      {/*negative headerheight necessary to account for upsell video banner*/}
      <YStack gap={'$lg'}>
        {/* Members get theirs inside the dark band above. */}
        {!holdsAMembership && (
          <UpcomingBookingsSection bookings={upcomingBookings} />
        )}

        {/* On the page's own surface rather than on the dark band above it:
            that band is the pitch, and an outstanding payment is the opposite
            of a pitch — they've already bought. */}
        {pendingMembership && (
          <PendingPaymentBanner
            pendingMembership={pendingMembership}
            onCtaClick={() =>
              router.push(`/my-memberships/${pendingMembership.id}`)
            }
          />
        )}
        <YStack>
          <PSText.Title>Handpicked experiences</PSText.Title>
        </YStack>
        {error && <PSHomeSectionError kind={error.kind} onRetry={retry} />}
        {isLoading && (
          <Carousel>
            <EventCardsSkeleton />
          </Carousel>
        )}
        {data &&
          (data.editorialPicks.length === 0 ? (
            <PSHomeSectionEmpty onRefresh={onRefresh} />
          ) : (
            <Carousel>
              {data.editorialPicks.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onPress={() => router.push(`/events/${event.id}`)}
                />
              ))}
            </Carousel>
          ))}
      </YStack>

      {/*Access Pass Section*/}
      {!holdsAMembership && (
        <YStack
          mx={-space.lg}
          bg={'$color.brandDark'}
          mt={'$space.xxl'}
          p={'$space.lg'}
          gap={'$space.xl'}
        >
          {showsAccessPassIntro && (
            <>
              <AccessPassIntro />
              <Divider isBackgroundDark />
            </>
          )}
          <YStack>
            <AccessPassExclusiveHeading isMember={false} />
            <YStack gap={'$lg'} mt={'$space.md'}>
              <YStack gap={'$space.xs'}>
                <PSText.Title color={'$color.textWhite'}>
                  Get first dibs and exclusive access
                </PSText.Title>
              </YStack>
              {error && (
                <PSHomeSectionError
                  kind={error.kind}
                  onRetry={retry}
                  isBackgroundDark
                />
              )}
              {isLoading && (
                <Carousel isBackgroundDark>
                  <EventCardsSkeleton />
                </Carousel>
              )}
              {data &&
                (data.accessPassExclusive.length === 0 ? (
                  <APEmptySection />
                ) : (
                  <Carousel isBackgroundDark>
                    {data.accessPassExclusive.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        panelBgColor="$panelTranslucent"
                        textColor="light"
                        onPress={() => router.push(`/events/${event.id}`)}
                      />
                    ))}
                  </Carousel>
                ))}
            </YStack>
          </YStack>
          {hasPlansOnOffer(upsell) && (
            <>
              <Divider isBackgroundDark />
              <View mb={'$space.md'}>
                <MembershipUpsellSection
                  upsell={upsell}
                  onSelectMembership={(plan) =>
                    router.push({
                      pathname: '/memberships',
                      params: { section: 'plans' },
                    })
                  }
                />
              </View>
            </>
          )}
        </YStack>
      )}

      {/*all experiences — the heading and tabs; the grid itself is the list*/}
      <YStack gap={'$space.lg'} mt={'$space.xxl'} mb={'$space.lg'}>
        <PSText.Title>
          {ALL_EXPERIENCES_HEADING[experiences.filter]}
        </PSText.Title>
        <Tabs
          options={EXPERIENCE_FILTERS}
          value={experiences.filter}
          onChange={onChangeExperienceFilter}
        />
      </YStack>
    </>
  );

  // Everything the grid says when it isn't rows: its bones, its failures, its
  // empty answer. All of it sits below the rows, which is where the list's footer
  // is — so the footer carries it, and the page's own footer closes the page out.
  const listFooter = (
    <YStack>
      {experiences.isLoading && (
        <XStack flexWrap="wrap" gap="$md">
          <EventCardsSkeleton count={GRID_SKELETON_COUNT} width={gridColW} />
        </XStack>
      )}
      {/* The next page's bones continue the grid, so they take the row gap. */}
      {experiences.isLoadingMore && (
        <XStack flexWrap="wrap" gap="$md" mt={'$space.md'}>
          <EventCardsSkeleton
            count={GRID_LOAD_MORE_SKELETON_COUNT}
            width={gridColW}
          />
        </XStack>
      )}
      {/* A failure with NO rows DRAWN is the whole grid; with rows it stays out
          of the way and the toast above carries it — see the hook. Row COUNT,
          not null: a filter that legitimately returned none and then failed to
          re-read holds `events` of [], which is still nothing to look at. */}
      {experiences.error && !experiences.events?.length && (
        <PSHomeSectionError
          kind={experiences.error.kind}
          onRetry={experiences.retry}
        />
      )}
      {/* A day filter matching nothing is a normal answer, not a failure, so it
          says so rather than leaving the tabs over blank space — but only when
          nothing failed. Ungated, a failed re-read of an empty filter states
          "nothing is listed" about a fetch that never landed. */}
      {!experiences.error && experiences.events?.length === 0 && (
        <Text color="$textDarkMuted" textAlign="center" my={'$space.xl'}>
          Nothing on those days. Try another tab.
        </Text>
      )}
      {/* Only ever a retry: scrolling asks for every other page. WHICH call
          depends on what failed — with a cursor left it was a page, and that
          cursor still points at it; with none it was `refresh` re-reading page
          1, and `loadMore` would no-op on the absent cursor before it could
          even clear the error, leaving nothing on screen to recover with. */}
      {experiences.error && !!experiences.events?.length && (
        <View alignSelf="center" mt={'$space.lg'}>
          <PSLinkButton
            title="Retry"
            onPress={
              experiences.hasMore ? experiences.loadMore : experiences.retry
            }
          />
        </View>
      )}
      <Footer />
    </YStack>
  );

  return (
    <>
      <Head>
        <title>Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: false,
          headerTitle: () => <></>,
          headerStyle: {
            backgroundColor: user?.membership
              ? getTokenValue('$color.brandDark')
              : getTokenValue('$color.brandDark'),
          },
          headerLeft: () => logo,
          headerRight: () => authControl,
          unstable_headerLeftItems: () => [
            { type: 'custom', element: logo, hidesSharedBackground: true },
          ],
          unstable_headerRightItems: () => [
            {
              type: 'custom',
              element: authControl,
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      <StatusBar style="light" />
      <PageFlatList
        data={experiences.events ?? NO_EVENTS}
        keyExtractor={(event) => event.id}
        renderItem={renderExperience}
        numColumns={GRID_COLUMNS}
        columnWrapperStyle={{ gap: space.md }}
        ItemSeparatorComponent={GridRowGap}
        ListHeaderComponent={listHeader}
        ListFooterComponent={listFooter}
        headerTransparent={false}
        onEndReached={onEndReached}
        onEndReachedThreshold={END_REACHED_THRESHOLD}
        isRefreshing={isRefreshing}
        onRefresh={onRefresh}
      />
    </>
  );
}
